import { plainToInstance } from 'class-transformer'
import {
    GetDomainNotificationABTestResponse,
    GetNamedNotificationTemplateResponse,
    GetNotificationResponse,
    GetNotificationScheduleResponse,
    GetNotificationTemplateChannelsResponse,
} from '@pushly/models/lib/structs/notifications'
import { NotificationDeliveryType } from '../../../enums/notification-delivery-type'
import { NotificationDeliveryWindow } from '../../../enums/notification-delivery-window'

// Re-export domain helpers for convenience
export {
    isTzDelivery,
    isTzDeliveryInProgress,
    canShowStats,
    isDelivered,
    isPartialFailure,
    isNotificationEditable,
    getLegacyAudienceType,
} from '@pushly/models/lib/utils/notification-helpers'

export function parseNotificationResponse(data: object): GetNotificationResponse {
    const result = plainToInstance(GetNotificationResponse, data, {
        excludeExtraneousValues: false,
    })

    if (Array.isArray(result.schedules) && result.schedules.length > 0) {
        const scheduleStatus = result.schedules[0].status
        if (scheduleStatus) {
            result.status = scheduleStatus
        }
    }

    return result
}

export function parseScheduleResponse(data: object): GetNotificationScheduleResponse {
    return plainToInstance(GetNotificationScheduleResponse, data, {
        excludeExtraneousValues: false,
    })
}

export function parseTestResponse(data: Record<string, unknown>): GetDomainNotificationABTestResponse {
    return plainToInstance(GetDomainNotificationABTestResponse, data, {
        excludeExtraneousValues: false,
    })
}

export function parseNamedNotificationTemplateResponse(data: object): GetNamedNotificationTemplateResponse {
    return plainToInstance(GetNamedNotificationTemplateResponse, data, {
        excludeExtraneousValues: false,
    })
}

export function convertNamedTemplateResponseToNotificationResponse(
    template: GetNamedNotificationTemplateResponse,
): GetNotificationResponse {
    const templateChannels = template.template?.channels
    const defaultChannel = templateChannels?.default

    return parseNotificationResponse({
        delivery_spec: {
            type: NotificationDeliveryType.IMMEDIATE,
            window: NotificationDeliveryWindow.STANDARD,
            ttl_seconds: template.template_ttl_spec?.ttl_seconds,
            ttl_metric: template.template_ttl_spec?.ttl_metric,
        },
        audience: {
            segment_ids: template.template_segment_ids?.length ? Array.from(template.template_segment_ids) : [],
            excluded_segment_ids: template.template_excluded_segment_ids?.length
                ? Array.from(template.template_excluded_segment_ids)
                : undefined,
        },
        template: {
            channels: {
                ...templateChannels,
                default: {
                    ...defaultChannel,
                    keywords: template.template?.keywords,
                    domain_id: template.domain_id,
                },
            },
            keywords: template.template?.keywords?.length ? Array.from(template.template.keywords) : [],
            name: template.name,
            description: template.description,
        },
        channels: template.channels?.length ? Array.from(template.channels) : [],
    })
}

export function getAltNativeUrl(
    channels?: GetNotificationTemplateChannelsResponse
): string | undefined {
    const defaultChannel = channels?.default
    const nativeIos = channels?.native_ios
    const nativeAndroid = channels?.native_android

    if (!nativeIos && !nativeAndroid) return undefined

    const nativeLandingUrl = nativeAndroid?.landing_url ?? nativeIos?.landing_url
    if (nativeLandingUrl && nativeLandingUrl !== defaultChannel?.landing_url) {
        return nativeLandingUrl
    }

    return undefined
}
